const ACTIVE = 'active' as const;
const IDLE = 'idle' as const;

type State = typeof ACTIVE | typeof IDLE;
type Listener = () => void;

const DEFAULT_INITIAL_STATE: State = ACTIVE;

const DEFAULT_ACTIVITY_EVENTS = [
  'click',
  'mousemove',
  'keydown',
  'DOMMouseScroll',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'focus',
] as const;

const DEFAULT_INACTIVITY_EVENTS: readonly string[] = [];

const DEFAULT_IGNORED_EVENTS_WHEN_IDLE: readonly string[] = ['mousemove'];

let hidden: keyof Document | undefined;
let visibilityChangeEvent: string | undefined;

if (typeof document.hidden !== 'undefined') {
  hidden = 'hidden';
  visibilityChangeEvent = 'visibilitychange';
} else {
  const prefixes = ['webkit', 'moz', 'ms'];
  for (const prefix of prefixes) {
    const prop = `${prefix}Hidden` as keyof Document;
    if (prop in document) {
      hidden = prop;
      visibilityChangeEvent = `${prefix}visibilitychange`;
      break;
    }
  }
}

interface ActivityDetectorOptions {
  activityEvents?: readonly string[];
  inactivityEvents?: readonly string[];
  ignoredEventsWhenIdle?: readonly string[];
  timeToIdle?: number;
  initialState?: State;
  autoInit?: boolean;
}

interface ActivityDetector {
  on(eventName: State, listener: Listener): () => void;
  stop(): void;
  init(firstState?: State): void;
}

const activityDetector = ({
  activityEvents = DEFAULT_ACTIVITY_EVENTS,
  inactivityEvents = DEFAULT_INACTIVITY_EVENTS,
  ignoredEventsWhenIdle = DEFAULT_IGNORED_EVENTS_WHEN_IDLE,
  timeToIdle = 30000,
  initialState = DEFAULT_INITIAL_STATE,
  autoInit = true,
}: ActivityDetectorOptions = {}): ActivityDetector => {
  const listeners: Record<State, Listener[]> = {
    [ACTIVE]: [],
    [IDLE]: [],
  };

  let state: State = initialState;
  let timer: ReturnType<typeof setTimeout>;

  const setState = (newState: State) => {
    clearTimeout(timer);

    if (newState === ACTIVE) {
      timer = setTimeout(() => setState(IDLE), timeToIdle);
    }

    if (state !== newState) {
      state = newState;
      listeners[state].forEach((l) => l());
    }
  };

  const handleUserActivityEvent = (event: Event) => {
    if (state === ACTIVE || !ignoredEventsWhenIdle.includes(event.type)) {
      setState(ACTIVE);
    }
  };

  const handleUserInactivityEvent = () => {
    setState(IDLE);
  };

  const handleVisibilityChangeEvent = () => {
    if (hidden) {
      setState(document[hidden] ? IDLE : ACTIVE);
    }
  };

  const init = (firstState: State = DEFAULT_INITIAL_STATE) => {
    setState(firstState);

    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, handleUserActivityEvent),
    );

    inactivityEvents
      .filter((e) => e !== 'visibilitychange')
      .forEach((eventName) => window.addEventListener(eventName, handleUserInactivityEvent));

    if (inactivityEvents.includes('visibilitychange') && visibilityChangeEvent) {
      document.addEventListener(visibilityChangeEvent, handleVisibilityChangeEvent);
    }
  };

  const on = (eventName: State, listener: Listener) => {
    listeners[eventName].push(listener);

    return () => {
      const index = listeners[eventName].indexOf(listener);
      if (index >= 0) listeners[eventName].splice(index, 1);
    };
  };

  const stop = () => {
    listeners[ACTIVE] = [];
    listeners[IDLE] = [];

    clearTimeout(timer);

    activityEvents.forEach((eventName) =>
      window.removeEventListener(eventName, handleUserActivityEvent),
    );

    inactivityEvents.forEach((eventName) =>
      window.removeEventListener(eventName, handleUserInactivityEvent),
    );

    if (visibilityChangeEvent) {
      document.removeEventListener(visibilityChangeEvent, handleVisibilityChangeEvent);
    }
  };

  if (autoInit) {
    init(initialState);
  }

  return { on, stop, init };
};

export default activityDetector;
